import React from 'react';
import {AlertCircle, CircleCheck, Clock, FileCheck, MessageSquare, UserCheck} from 'lucide-react';
import {formatDate} from '../../utils/helpers';

/**
 * Component for displaying submission status
 * Enhanced to support foundational, preparatory, and middle stages
 *
 * @param {Object} props
 * @param {Object} props.submission - Submission data
 * @param {boolean} props.isPreparatory - Whether this is for preparatory stage
 * @param {boolean} props.isMiddle - Whether this is for middle stage
 */
const SubmissionStatus = ({submission, isPreparatory = false, isMiddle = false}) => {
    // Calculate completion status based on stage requirements
    const getCompletionStatus = () => {
        if (!submission) return {isComplete: false, progress: 0};

        const hasFile = !!submission.file_path;
        const hasSelfAssessment = !!submission.self_assessment;
        const hasPeerAssessment = !!submission.peer_assessment;
        const hasTeacherFeedback = !!submission.teacher_feedback || !!submission.observational_notes;

        // Different requirements for different stages
        if (isMiddle || isPreparatory) {
            // Middle and Preparatory stages require all components
            const totalSteps = 4;
            const completedSteps =
                (hasFile ? 1 : 0) +
                (hasSelfAssessment ? 1 : 0) +
                (hasPeerAssessment ? 1 : 0) +
                (hasTeacherFeedback ? 1 : 0);

            return {
                isComplete: completedSteps === totalSteps,
                progress: Math.round((completedSteps / totalSteps) * 100)
            };
        } else {
            // Foundational stage mainly requires file submission
            // But we track progress of all components
            const totalSteps = 4;
            const completedSteps =
                (hasFile ? 1 : 0) +
                (hasSelfAssessment ? 1 : 0) +
                (hasPeerAssessment ? 1 : 0) +
                (hasTeacherFeedback ? 1 : 0);

            return {
                isComplete: hasFile, // Only file is required for "complete" status
                progress: Math.round((completedSteps / totalSteps) * 100)
            };
        }
    };

    const {isComplete, progress} = getCompletionStatus();

    // Check if assignment has been submitted (file uploaded)
    const hasFileSubmission = submission && submission.file_path;

    // Define color based on progress
    const getProgressColorClass = () => {
        if (progress < 25) return 'bg-red-500';
        if (progress < 75) return 'bg-yellow-500';
        return 'bg-green-500';
    };

    return (
        <div className="bg-white rounded-2xl shadow-md overflow-hidden border border-gray-200">
            <div className="p-6 border-b border-gray-100 bg-gray-50">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#D67C2C]">
                        <CircleCheck className="w-5 h-5 text-white"/>
                    </div>
                    <div>
                        <h2 className="text-xl font-semibold text-gray-900 font-[poppins]">Submission Status</h2>
                        {(isMiddle || isPreparatory) && (
                            <p className="text-sm text-gray-600 mt-1">
                                {isMiddle ? 'Middle Stage' : 'Preparatory Stage'} assessment requires completion of all components
                            </p>
                        )}
                    </div>
                </div>
            </div>

            <div className="p-6">
                {/* Progress bar (visible for all stages) */}
                <div className="mb-6">
                    <div className="flex justify-between items-center mb-3">
                        <span className="text-sm font-semibold text-gray-700">
                            {isComplete ? 'Complete' : 'Progress'}
                        </span>
                        <span className="text-sm font-semibold text-gray-700 bg-gray-100 px-2 py-1 rounded-full">{progress}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                        <div
                            className={`h-3 rounded-full transition-all duration-300 ${getProgressColorClass()}`}
                            style={{width: `${progress}%`}}
                        ></div>
                    </div>
                </div>

                <div className="space-y-4">
                    <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                        <div className="flex justify-between items-center">
                            <span className="text-sm font-semibold text-gray-700">Overall Status</span>
                            <div>
                                {hasFileSubmission ? (
                                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800 border border-green-200">
                                        <CircleCheck size={16} className="mr-2"/>
                                        Submitted
                                    </span>
                                ) : (
                                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800 border border-yellow-200">
                                        <Clock size={16} className="mr-2"/>
                                        Pending
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    {submission && (
                        <>
                            <div
                                className="bg-gray-50 px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 border-b border-gray-200">
                                <dt className="text-sm font-medium text-gray-500">File Submission</dt>
                                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                    {submission.file_path ? (
                                        <div className="flex flex-col">
                                            <span
                                                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                <FileCheck size={14} className="mr-1"/>
                                                Completed on {formatDate(submission.submitted_at)}
                                            </span>
                                            <a
                                                href="#"
                                                className="text-xs text-indigo-600 hover:text-indigo-800 mt-1"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    // Logic to view submission file
                                                    window.open(submission.file_path, '_blank');
                                                }}
                                            >
                                                View Submission
                                            </a>
                                        </div>
                                    ) : (
                                        <span
                                            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                            <AlertCircle size={14} className="mr-1"/>
                                            Missing
                                        </span>
                                    )}
                                </dd>
                            </div>

                            <div
                                className="bg-white px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 border-b border-gray-200">
                                <dt className="text-sm font-medium text-gray-500">Self-assessment</dt>
                                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                    {submission.self_assessment ? (
                                        <span
                                            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                            <FileCheck size={14} className="mr-1"/>
                                            Completed
                                        </span>
                                    ) : (
                                        <span
                                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                                isMiddle || isPreparatory ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'
                                            }`}>
                                            {isMiddle || isPreparatory ?
                                                <><AlertCircle size={14} className="mr-1"/>Required</> :
                                                'Not submitted'
                                            }
                                        </span>
                                    )}
                                </dd>
                            </div>

                            <div
                                className="bg-gray-50 px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 border-b border-gray-200">
                                <dt className="text-sm font-medium text-gray-500">Peer assessment</dt>
                                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                    {submission.peer_assessment ? (
                                        <span
                                            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                            <UserCheck size={14} className="mr-1"/>
                                            Completed
                                        </span>
                                    ) : (
                                        <span
                                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                                isMiddle || isPreparatory ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'
                                            }`}>
                                            {isMiddle || isPreparatory ?
                                                <><AlertCircle size={14} className="mr-1"/>Required</> :
                                                'Not submitted'
                                            }
                                        </span>
                                    )}
                                </dd>
                            </div>

                            <div className="bg-white px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                <dt className="text-sm font-medium text-gray-500">Teacher feedback</dt>
                                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                    {submission.teacher_feedback || submission.observational_notes ? (
                                        <span
                                            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                            <MessageSquare size={14} className="mr-1"/>
                                            Received
                                        </span>
                                    ) : (
                                        <span
                                            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                            <Clock size={14} className="mr-1"/>
                                            Awaiting Teacher
                                        </span>
                                    )}
                                </dd>
                            </div>

                            {/* HPC Report Link (Middle Stage Only) */}
                            {isMiddle && (
                                <div className="mt-4 pt-4 border-t border-gray-200">
                                    <div className="flex items-center">
                                        <div className="flex-shrink-0 mr-3">
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-indigo-500"
                                                 fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                                            </svg>
                                        </div>
                                        <div>
                                            <p className="text-sm font-medium text-gray-700">Holistic Progress Card</p>
                                            <p className="text-xs text-gray-500">
                                                Your HPC report will be available once assessments are complete.
                                            </p>
                                            {/*{isComplete ? (*/}
                                            {/*    <a*/}
                                            {/*        href="#"*/}
                                            {/*        className="text-xs text-indigo-600 hover:text-indigo-800 mt-1 inline-block"*/}
                                            {/*        onClick={(e) => {*/}
                                            {/*            e.preventDefault();*/}
                                            {/*            // Logic to view HPC report*/}
                                            {/*            alert("This will open your Holistic Progress Card (HPC)");*/}
                                            {/*        }}*/}
                                            {/*    >*/}
                                            {/*        View HPC Report →*/}
                                            {/*    </a>*/}
                                            {/*) : (*/}
                                            {/*    <span className="text-xs text-gray-500 mt-1 inline-block">*/}
                                            {/*        Available after all components are complete*/}
                                            {/*    </span>*/}
                                            {/*)}*/}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SubmissionStatus;